import { Dispatch } from 'redux';
import Config from 'react-native-config';
import base64 from 'react-native-base64';
import FileServices from 'services/FileServices';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import {
  ActionPayloadRequest,
  AdapterSource,
  DFDHeadersObject,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import { RootState } from 'store/types';
import { microbiologyDetailsDownloadDataSelector } from './selectors';
import { ShareMicrobiologyResults, TestResult, TestResultFocused } from '../types';
import { createSearchParamsString } from '../utils';

/**
 * Microbiology Historical
 */
export const GET_MICROBIOLOGY_HISTORICAL = 'TEST_RESULTS.GET_MICROBIOLOGY_HISTORICAL';
export const GET_MICROBIOLOGY_HISTORICAL_SUCCESS =
  'TEST_RESULTS.GET_MICROBIOLOGY_HISTORICAL_SUCCESS';
export const GET_MICROBIOLOGY_HISTORICAL_FAIL = 'TEST_RESULTS.GET_MICROBIOLOGY_HISTORICAL_FAIL';

interface GetMicrobiologyHistorical {
  source: string;
  nameNcid: string;
}

export const getMicrobiologyHistorical = (
  params: GetMicrobiologyHistorical,
  adapterSource = AdapterSource.UTAH
) => {
  let client = 'testResults';
  const request: ActionPayloadRequest = {
    method: 'get',
    url: '/microbiology/historical',
    params
  };

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';
    request.url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.MICROBIOLOGIES,
      DispatcherEndpoint.HISTORICAL
    );
    request.headers = {
      [DFDHeaders.filterSource]: params.source,
      [DFDHeaders.mhpAdapter]: adapterSource,
      [DFDHeaders.mhpTestResultNcid]: params.nameNcid
    };
    delete request.params;
  }

  return {
    type: GET_MICROBIOLOGY_HISTORICAL,
    payload: {
      client,
      request
    }
  };
};

/**
 * Download Microbiology Results
 */
export const DOWNLOAD_MICROBIOLOGY_DETAILS = 'TEST_RESULTS.DOWNLOAD_MICROBIOLOGY_DETAILS';
export const DOWNLOAD_MICROBIOLOGY_DETAILS_SUCCESS =
  'TEST_RESULTS.DOWNLOAD_MICROBIOLOGY_DETAILS_SUCCESS';
export const DOWNLOAD_MICROBIOLOGY_DETAILS_FAIL = 'TEST_RESULTS.DOWNLOAD_MICROBIOLOGY_DETAILS_FAIL';

export const downloadMicrobiologyDetails = (
  eventId: string,
  testResult: TestResult | TestResultFocused
) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (microbiologyDetailsDownloadDataSelector(state)[eventId] === 'pending') {
    return;
  }

  dispatch({ type: DOWNLOAD_MICROBIOLOGY_DETAILS, payload: { id: eventId } });

  const searchParams = createSearchParamsString({
    eventId,
    source: testResult.source
  });
  const auditData = base64.encode(
    JSON.stringify({
      date: testResult.testDate,
      message: testResult.name
    })
  );
  let url = `${Config.TEST_RESULTS_BASE_URL}/microbiology/detail/download?${searchParams}`;
  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.MICROBIOLOGIES,
      DispatcherEndpoint.ID_DOWNLOAD,
      true
    );
  }

  let headers = getDownloadHeadersWithCurrentAccountConsumerId(state, url);
  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    headers = {
      ...headers,
      [DFDHeaders.eventIds]: eventId,
      [DFDHeaders.filterSource]: testResult.source,
      [DFDHeaders.mhpAccept]: 'application/pdf',
      [DFDHeaders.mhpAdapter]: testResult.adapterSource,
      [DFDHeaders.mhpAuditData]: auditData
    };
  } else {
    headers[DFDHeaders.auditData] = auditData;
  }

  try {
    const response = await FileServices.download(
      `microbiology-details-${eventId}.pdf`,
      url,
      headers
    );

    dispatch({ type: DOWNLOAD_MICROBIOLOGY_DETAILS_SUCCESS, payload: { id: eventId, response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_MICROBIOLOGY_DETAILS_FAIL, payload: { id: eventId }, error: err });
  }
};

/**
 * Share Microbiology Results
 */
export const SHARE_MICROBIOLOGY_RESULTS = 'TEST_RESULTS.SHARE_MICROBIOLOGY_RESULTS';
export const SHARE_MICROBIOLOGY_RESULTS_SUCCESS = 'TEST_RESULTS.SHARE_MICROBIOLOGY_RESULTS_SUCCESS';
export const SHARE_MICROBIOLOGY_RESULTS_FAIL = 'TEST_RESULTS.SHARE_MICROBIOLOGY_RESULTS_FAIL';

export const shareMicrobiologyResults = (
  testResult: TestResult | TestResultFocused,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string
) => {
  const { adapterSource = AdapterSource.UTAH, eventId, source, name, testDate } = testResult;
  const auditData = base64.encode(
    JSON.stringify({
      date: testDate,
      message: name
    })
  );
  const data: ShareMicrobiologyResults = {
    toAddress,
    message,
    useSecureEmail,
    contactPhoneNumber
  };
  let client = 'testResults';
  let url = `/microbiology/detail/share?source=${source}&eventId=${eventId}`;
  let headers: DFDHeadersObject = {
    [DFDHeaders.mhpAccept]: 'application/pdf'
  };
  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';
    url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.MICROBIOLOGIES,
      DispatcherEndpoint.ID_TRANSMIT
    );
    headers = {
      ...headers,
      [DFDHeaders.eventIds]: eventId,
      [DFDHeaders.filterSource]: source,
      [DFDHeaders.mhpAdapter]: adapterSource,
      [DFDHeaders.mhpAuditData]: auditData
    };
    data.subject = '';
  } else {
    headers[DFDHeaders.auditData] = auditData;
  }
  const dataObj = {
    type: SHARE_MICROBIOLOGY_RESULTS,
    payload: {
      client,
      request: {
        method: 'post',
        url,
        data,
        headers
      }
    }
  };

  return dataObj;
};
